import {
  Alert,
  AlertTitle,
  Button,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Paper,
} from "@mui/material";
import { AnyObjectSchema, boolean, number, object, string } from "yup";
import { buildPermission } from "portal/utils/auth";
import { capitalize, titleCase } from "portal/utils/strings";
import { classes } from "portal/theme/theme";
import { ConfigAuditLog } from "./ConfigAuditLog";
import { ConfigNode } from "protos/config/api/config_service";
import { Field, Form, Formik, FormikErrors, FormikTouched } from "formik";
import {
  filterConfigTree,
  getConfigChanges,
  getNodeFromPath,
  getParentPath,
  includeAncestors,
  isBooleanNode,
  isFloatNode,
  isIntNode,
  isLeafNode,
  isListNode,
  isStringNode,
  isUintNode,
} from "portal/utils/configs";
import { TextField as FormikTextField, Select } from "formik-mui";
import {
  MutationActionCreatorResult,
  QueryStatus,
  skipToken,
} from "@reduxjs/toolkit/query";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { SwitchWithLabel } from "portal/components/SwitchWithLabel";
import { Tree } from "./ConfigTree";
import { UnsyncedKeyWarning } from "./UnsyncedKeyWarning";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "../auth/WithAuthorizationRequired";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { usePageRef } from "../Page";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import React, {
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import SaveIcon from "@mui/icons-material/SaveOutlined";

interface ConfigPaths {
  changedPathsTree?: ConfigNode | undefined;
  changedPathsAncestors?: string[] | undefined;
  addedPaths?: string[] | undefined;
  changedPaths?: string[] | undefined;
  removedPaths?: string[] | undefined;
}

interface BaseProps {
  basePath?: string;
  readOnly?: boolean;
}

interface SerialProps extends BaseProps {
  serial: string | undefined;
  customerSerial: string | undefined;
}

interface TemplateProps extends BaseProps {
  robotClass?: string;
}

const isPropsSerial = (
  props: SerialProps | TemplateProps
): props is SerialProps => "serial" in props;

const isPropsTemplate = (
  props: SerialProps | TemplateProps
): props is TemplateProps => "robotClass" in props;

const _Config: FunctionComponent<SerialProps | TemplateProps> = ({
  basePath: inputPath,
  readOnly = false,
  ...props
}) => {
  const { t } = useTranslation();
  const basePath = inputPath?.replace(/\/+$/, "") ?? "";
  const isSerial = isPropsSerial(props);
  const isTemplate = isPropsTemplate(props);
  const useSetValueMutation = isTemplate
    ? portalApi.endpoints.setConfigTemplateValue.useMutation
    : portalApi.endpoints.setConfigValue.useMutation;
  const useDeletePathMutation = isTemplate
    ? portalApi.endpoints.deleteConfigTemplatePath.useMutation
    : portalApi.endpoints.deleteConfigPath.useMutation;
  let serial: string | undefined;
  let customerSerial: string | undefined;
  if (isTemplate) {
    serial = props.robotClass;
  }
  if (isSerial) {
    serial = props.serial;
    customerSerial = props.customerSerial;
  }

  const pageRef = usePageRef();
  const [isAuditLogOpen, setAuditLogOpen] = useState(false);

  const { data: configData } = useQueryPopups(
    portalApi.endpoints.getConfig.useQuery(
      isSerial && serial ? { serial } : skipToken
    )
  );
  const { data: templateData } = useQueryPopups(
    portalApi.endpoints.getConfigTemplate.useQuery(
      isTemplate && serial ? { serial } : skipToken
    )
  );
  const config = isTemplate ? templateData : configData?.config;
  const template = isSerial ? configData?.template : undefined;
  const unsyncedKeys = isSerial ? configData?.unsyncedKeys : [];

  const canUpdate = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.update,
        PermissionResource.configs,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.configs,
        PermissionDomain.all
      ),
    ],
  });

  // get default config path from URL
  const { pathname, state: locationState } = useLocation();
  let path = pathname.replaceAll(basePath, "");
  // fix any double slashed
  path = path.replaceAll(/\/{2,}/g, "/");
  // remove leading slash if any
  path = path.replace(/^\//, "");
  const [selectedPath, setSelectedPath] = useState<string>(path || "");

  // keep config path up to date
  const navigate = useNavigate();
  useEffect(() => {
    if (selectedPath) {
      navigate(`${basePath}/${selectedPath}`, {
        replace: true,
        state: locationState,
      });
    }
  }, [basePath, navigate, selectedPath, locationState]);

  const {
    changedPathsTree,
    changedPathsAncestors,
    addedPaths,
    changedPaths,
    removedPaths,
  } = useMemo<ConfigPaths>(() => {
    if (!config || !template) {
      return {};
    }
    const { addedPaths, changedPaths, removedPaths, allChangedPaths } =
      getConfigChanges(config, template);
    const changedPathsAncestors = includeAncestors(allChangedPaths);
    return {
      changedPathsTree: filterConfigTree(config, changedPathsAncestors, "", {
        ignoreRoot: true,
        showChildren: false,
      }),
      changedPathsAncestors,
      addedPaths,
      changedPaths,
      removedPaths,
    };
  }, [config, template]);

  const selectedNode = getNodeFromPath(config, selectedPath);
  const parentPath = getParentPath(selectedPath);
  const parentNode = getNodeFromPath(config, parentPath);
  const templateNode = getNodeFromPath(template, selectedPath);

  // handle update value
  const [setConfigValue] = useMutationPopups(useSetValueMutation(), {
    success: capitalize(
      t("utils.actions.savedLong", {
        subject: t("models.configs.value_one"),
      })
    ),
  });
  const updateItem = async (
    value: any,
    reason: string
  ): Promise<MutationActionCreatorResult<any> | undefined> => {
    if (!serial || !config) {
      return;
    }
    const response = await setConfigValue({
      serial,
      path: selectedPath,
      value,
      reason,
      configType: selectedNode?.def?.type,
    });
    if (isListNode(selectedNode)) {
      setSelectedPath(`${selectedPath}/${value}`);
    }
    return response;
  };

  // handle list delete
  const [deleteValue, { status }] = useMutationPopups(useDeletePathMutation(), {
    success: capitalize(
      t("utils.actions.deletedLong", {
        subject: t("models.configs.key_one"),
      })
    ),
  });

  const isDeleting = status === QueryStatus.pending;
  const deleteItem = async (
    path: string,
    reason: string,
    validateForm: (values?: any) => Promise<FormikErrors<any>>,
    setTouched: (touched: FormikTouched<any>, shouldValidate?: boolean) => any
  ): Promise<void> => {
    if (!serial) {
      return;
    }
    const errors = await validateForm();

    if (Object.keys(errors).length === 0) {
      await deleteValue({ serial, path, reason });
      setSelectedPath(parentPath);
    } else {
      // show errors to the user
      await setTouched(
        Object.fromEntries(
          Object.entries(errors).map((entry) => [entry[0], true])
        ),
        true
      );
    }
  };

  // generate appropriate field
  let fields: ReactElement | null;
  let defaultValue: string | number | boolean = "";
  let schema: AnyObjectSchema | undefined;
  let value: string | number | boolean = "";
  const units = selectedNode?.def?.units;
  let hint = selectedNode?.def?.hint;
  const isDeprecated = hint && hint.toLowerCase().includes("deprecated");
  const deprecationWarning = isDeprecated ? hint : undefined;
  if (isDeprecated) {
    hint = undefined;
  }
  const deprecationAlert = deprecationWarning ? (
    <Alert severity="error">{deprecationWarning}</Alert>
  ) : (
    <></>
  );
  const reasonField = isTemplate ? (
    <></>
  ) : (
    <Field
      fullWidth
      component={FormikTextField}
      name="reason"
      label={t("views.admin.config.reason")}
      disabled={readOnly || !canUpdate}
      required
    />
  );
  const reasonSchema: { [x: string]: any } = isTemplate
    ? {}
    : {
        reason: string().required(t("utils.form.required")),
      };
  if (isBooleanNode(selectedNode)) {
    const formattedUnits = units ? ` ${units}` : "";
    fields = (
      <>
        <FormControl>
          <div className="flex gap-2">
            <Field
              component={SwitchWithLabel}
              type="checkbox"
              name="value"
              label={`${selectedNode?.name}${formattedUnits}`}
              disabled={readOnly || !canUpdate}
            />
          </div>
          {hint && <FormHelperText>{hint}</FormHelperText>}
          {deprecationAlert}
        </FormControl>
        {reasonField}
      </>
    );
    schema = object({
      value: boolean()
        .required(t("utils.form.required"))
        .typeError(t("utils.form.booleanType")),
      ...reasonSchema,
    });
    value = selectedNode?.value?.boolVal ?? false;
    defaultValue = templateNode?.value?.boolVal ?? false;
  } else if (isStringNode(selectedNode)) {
    const stringDef = selectedNode?.def?.stringDef;
    const sizeLimit = stringDef?.sizeLimit || Infinity; // zero means unlimited
    const choices = stringDef?.choices;
    if (Array.isArray(choices) && choices.length > 0) {
      fields = (
        <>
          <Field
            labelId={`field-${selectedNode?.name}`}
            component={Select}
            className={classes("min-w-52")}
            autoWidth
            name="value"
            label={selectedNode?.name}
            input={<OutlinedInput label={selectedNode?.name} />}
            formHelperText={{ children: hint }}
            disabled={readOnly || !canUpdate}
          >
            {choices.map((choice: string) => (
              <MenuItem key={choice} value={choice}>
                {choice}
                {units ? ` ${units}` : ""}
              </MenuItem>
            ))}
          </Field>
          {deprecationAlert}
          {reasonField}
        </>
      );
      schema = object({
        value: string(),
        ...reasonSchema,
      });
    } else {
      fields = (
        <>
          <Field
            component={FormikTextField}
            slotProps={{
              input: {
                endAdornment: units && (
                  <InputAdornment position="end">{units}</InputAdornment>
                ),
              },
            }}
            name="value"
            label={selectedNode?.name}
            helperText={hint}
            disabled={readOnly || !canUpdate}
          />
          {deprecationAlert}
          {reasonField}
        </>
      );
      schema = object({
        value: string()
          .test(
            "size_limit",
            t("utils.form.maxSize", { limit: sizeLimit }),
            (value) => (value ? value.length <= sizeLimit : true)
          )
          .typeError(t("utils.form.stringType")),
        ...reasonSchema,
      });
    }
    value = selectedNode?.value?.stringVal ?? "";
    defaultValue = templateNode?.value?.stringVal ?? "";
  } else if (isIntNode(selectedNode) || isUintNode(selectedNode)) {
    fields = (
      <>
        <Field
          component={FormikTextField}
          name="value"
          label={selectedNode?.name}
          slotProps={{
            input: {
              endAdornment: units && (
                <InputAdornment position="end">{units}</InputAdornment>
              ),
            },
            htmlInput: { inputMode: "numeric", pattern: "[0-9]+" },
          }}
          helperText={hint}
          disabled={readOnly || !canUpdate}
        />
        {deprecationAlert}
        {reasonField}
      </>
    );
    if (isIntNode(selectedNode)) {
      value = selectedNode?.value?.int64Val ?? 0;
      defaultValue = String(templateNode?.value?.int64Val ?? 0);
    } else {
      value = selectedNode?.value?.uint64Val ?? 0;
      defaultValue = String(templateNode?.value?.uint64Val ?? 0);
    }
    schema = object({
      value: number()
        .integer(t("utils.form.integerType"))
        .required(t("utils.form.required"))
        .typeError(t("utils.form.numberType")),
      ...reasonSchema,
    });
  } else if (isFloatNode(selectedNode)) {
    fields = (
      <>
        <Field
          component={FormikTextField}
          name="value"
          label={selectedNode?.name}
          slotProps={{
            input: {
              endAdornment: units && (
                <InputAdornment position="end">{units}</InputAdornment>
              ),
            },
            htmlInput: {
              inputMode: "numeric",
              pattern: "[0-9]+\\.?[0-9]*",
            },
          }}
          helperText={hint}
          disabled={readOnly || !canUpdate}
        />
        {deprecationAlert}
        {reasonField}
      </>
    );
    schema = object({
      value: number()
        .required(t("utils.form.required"))
        .typeError(t("utils.form.numberType")),
      ...reasonSchema,
    });
    value = selectedNode?.value?.floatVal ?? 0;
    defaultValue = templateNode?.value?.floatVal ?? 0;
  } else if (isListNode(selectedNode)) {
    if (readOnly || !canUpdate) {
      fields = <></>;
      schema = undefined;
    } else {
      fields = (
        <>
          <Field
            component={FormikTextField}
            name="value"
            label={t("components.config.newKey", {
              key: selectedNode?.name,
            })}
            helperText={hint}
          />
          {deprecationAlert}
          {reasonField}
        </>
      );
      schema = object({
        value: string()
          .required(t("utils.form.required"))
          .matches(/^[\w.-]+$/, t("components.config.stringReqs"))
          .typeError(t("utils.form.stringType")),
        ...reasonSchema,
      });
    }
  } else if (isListNode(parentNode)) {
    if (readOnly || !canUpdate) {
      fields = <></>;
      schema = undefined;
    } else {
      fields = reasonField;
      schema = object({
        ...reasonSchema,
      });
    }
  }

  const missingDefaults: Set<string> = new Set();
  if (removedPaths) {
    for (const path of removedPaths) {
      if (path.startsWith(selectedPath)) {
        const truncated = path.replace(`${selectedPath}/`, "");
        const segments = truncated.split("/");
        if (segments.length > 0) {
          const firstSegment = segments[0];
          if (!firstSegment) {
            continue;
          }
          missingDefaults.add(firstSegment);
        }
      }
    }
  }

  const showContent =
    isLeafNode(selectedNode) ||
    isListNode(selectedNode) ||
    isListNode(parentNode);

  return (
    <>
      <UnsyncedKeyWarning
        displaySerial={customerSerial ?? serial}
        config={config}
        unsyncedKeys={unsyncedKeys}
        className="max-h-60"
      />
      <div className="flex flex-col-reverse md:flex-row gap-8 h-full">
        <Tree
          className="w-full md:w-auto"
          config={config}
          initialShowChanged={Boolean(locationState?.showChanged)}
          onSelect={(path) => {
            const nextPath = Array.isArray(path) ? path[0] ?? "" : path ?? "";
            setSelectedPath(nextPath);
            pageRef?.current?.scrollTo(0, 0);
          }}
          selectedPath={selectedPath}
          addedPaths={addedPaths}
          removedPaths={removedPaths}
          changedPathsAncestors={changedPathsAncestors}
          changedPathsTree={changedPathsTree}
          isTemplate={isTemplate}
        />
        {showContent && serial && (
          <Paper key={selectedPath} className={classes("md:flex-grow", "p-8")}>
            {" "}
            <Formik
              enableReinitialize
              initialValues={{ value, reason: "" }}
              validationSchema={schema}
              onSubmit={async (values, { resetForm }) => {
                const response = await updateItem(
                  schema?.cast(values).value,
                  values.reason
                );
                if (response && !response.error) {
                  resetForm({ values });
                }
              }}
            >
              {({
                submitForm,
                isSubmitting,
                setFieldValue,
                values,
                initialValues,
                validateForm,
                setTouched,
              }) => (
                <Form className="flex flex-col items-start gap-4">
                  {fields}
                  <div className="flex items-center gap-4">
                    <Button
                      color="secondary"
                      variant="contained"
                      disabled={
                        readOnly ||
                        !canUpdate ||
                        values.value === initialValues.value ||
                        isDeleting
                      }
                      loading={isSubmitting}
                      onClick={submitForm}
                      startIcon={
                        isListNode(selectedNode) ? <AddIcon /> : <SaveIcon />
                      }
                    >
                      {isListNode(selectedNode)
                        ? t("utils.actions.addLong", {
                            subject: titleCase(t("models.configs.key_one")),
                          })
                        : t("utils.actions.save")}
                    </Button>
                    <Button
                      variant="text"
                      color="info"
                      onClick={() => setAuditLogOpen(true)}
                    >
                      {t("views.fleet.robots.config.auditLog.open")}
                    </Button>
                    <ConfigAuditLog
                      serial={serial}
                      path={selectedPath}
                      open={isAuditLogOpen}
                      onClose={() => setAuditLogOpen(false)}
                    />
                  </div>
                  {missingDefaults.size > 0 && isListNode(selectedNode) && (
                    <Alert severity="warning" className="mt-4 mr-8">
                      <AlertTitle>
                        {t("components.config.warnings.valueChanged.title")}
                      </AlertTitle>
                      {t("components.config.warnings.keyMissing.description", {
                        count: missingDefaults.size,
                        keys: [...missingDefaults].join(", "),
                      })}
                    </Alert>
                  )}
                  {isListNode(parentNode) && (
                    <>
                      <Button
                        color="error"
                        variant="contained"
                        disabled={readOnly || !canUpdate || isSubmitting}
                        loading={isDeleting}
                        onClick={() =>
                          deleteItem(
                            selectedPath,
                            values.reason,
                            validateForm,
                            setTouched
                          )
                        }
                        startIcon={<DeleteIcon />}
                      >
                        {t("utils.actions.deleteLong", {
                          subject: t("models.configs.key_one"),
                        })}
                      </Button>
                      {addedPaths?.includes(selectedPath) && (
                        <Alert severity="warning" className="mt-4 mr-8">
                          <AlertTitle>
                            {t("components.config.warnings.valueChanged.title")}
                          </AlertTitle>
                          {t("components.config.warnings.keyExtra.description")}
                          <Button
                            color="error"
                            variant="contained"
                            disabled={readOnly || !canUpdate || isSubmitting}
                            loading={isDeleting}
                            onClick={() =>
                              deleteItem(
                                selectedPath,
                                values.reason,
                                validateForm,
                                setTouched
                              )
                            }
                            startIcon={<DeleteIcon />}
                          >
                            {t("utils.actions.deleteLong", {
                              subject: t("models.configs.key_one"),
                            })}
                          </Button>
                        </Alert>
                      )}
                    </>
                  )}
                  {changedPaths?.includes(selectedPath) && (
                    <Alert severity="warning" className="mt-4 mr-8">
                      <AlertTitle>
                        {t("components.config.warnings.valueChanged.title")}
                      </AlertTitle>
                      {t(
                        "components.config.warnings.valueChanged.description",
                        {
                          default: String(defaultValue) || "<BLANK>",
                        }
                      )}
                      <Button
                        onClick={() => {
                          setFieldValue("value", defaultValue);
                          submitForm();
                        }}
                        disabled={readOnly || !canUpdate}
                      >
                        {t("utils.actions.resetLong", {
                          subject: t("models.configs.value_one"),
                        })}
                      </Button>
                    </Alert>
                  )}
                </Form>
              )}
            </Formik>
          </Paper>
        )}
      </div>
    </>
  );
};

export const Config = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.configs,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.configs,
        PermissionDomain.all
      ),
    ],
  },
  _Config
);
